import { useState } from 'react';
import { formatReferences } from '../api';

const STYLES = ['APA', 'MLA', 'Vancouver', 'Chicago', 'Harvard'];

function Spinner({ className = 'h-4 w-4' }) {
  return (
    <svg
      className={`animate-spin ${className}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Silently fail
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="opacity-0 group-hover:opacity-100 transition-opacity ml-2 flex-shrink-0
                 px-2 py-0.5 text-xs text-[#4d4d4d] hover:text-[#171717] rounded"
      style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
      aria-label="Copy reference"
      title="Copy"
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

export default function ReferenceFormatter({ papers = [] }) {
  const [selectedStyle, setSelectedStyle] = useState('APA');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [copyAllDone, setCopyAllDone] = useState(false);
  const [downloadDone, setDownloadDone] = useState(false);

  const hasPapers = papers.length > 0;

  const handleFormat = async () => {
    if (!hasPapers) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const mapped = papers.map((p) => ({
        id: p.id,
        title: p.title,
        authors: p.authors || [],
        year: (p.published_date || '').slice(0, 4) || null,
        url: p.doi_url || null,
        source: p.source || null,
      }));
      const res = await formatReferences(mapped, selectedStyle);
      setResult(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(', ')
        : typeof detail === 'string'
        ? detail
        : 'Failed to format references. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyAll = async () => {
    if (!result?.full_bibliography) return;
    try {
      await navigator.clipboard.writeText(result.full_bibliography);
      setCopyAllDone(true);
      setTimeout(() => setCopyAllDone(false), 2000);
    } catch {
      setError('Could not access clipboard. Please copy manually.');
    }
  };

  const handleDownload = () => {
    if (!result?.full_bibliography) return;
    const blob = new Blob([result.full_bibliography], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `references-${selectedStyle}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setDownloadDone(true);
    setTimeout(() => setDownloadDone(false), 2000);
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-[#171717]" style={{ letterSpacing: '-1.28px' }}>Reference Formatter</h2>
        <p className="text-sm text-[#4d4d4d] mt-1">
          Generate a formatted bibliography from your workspace papers in your preferred citation style.
        </p>
      </div>

      {!hasPapers ? (
        <div className="text-center py-12 text-[#808080]">
          <p className="text-4xl mb-3">📄</p>
          <p className="text-sm font-medium">No papers in workspace.</p>
          <p className="text-xs mt-1">Add papers to your workspace to format references.</p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            <p className="text-xs font-semibold text-[#808080] uppercase tracking-wide">Citation Style</p>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Citation style">
              {STYLES.map((style) => (
                <button
                  key={style}
                  onClick={() => setSelectedStyle(style)}
                  className="px-4 py-2 rounded-md text-sm font-medium transition-colors"
                  style={{
                    boxShadow: selectedStyle === style
                      ? 'rgba(0,0,0,0.12) 0px 0px 0px 2px'
                      : 'rgba(0,0,0,0.08) 0px 0px 0px 1px',
                    background: selectedStyle === style ? '#171717' : '#fff',
                    color: selectedStyle === style ? '#fff' : '#4d4d4d',
                  }}
                  aria-pressed={selectedStyle === style}
                >
                  {style}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleFormat}
              disabled={loading}
              className="px-5 py-2.5 bg-[#171717] text-white rounded-md font-medium text-sm
                         hover:bg-[#2e2e2e] disabled:opacity-50 disabled:cursor-not-allowed
                         transition-colors flex items-center gap-2"
            >
              {loading ? (
                <><Spinner /> Formatting…</>
              ) : (
                'Format References'
              )}
            </button>
            {hasPapers && (
              <span className="text-xs text-[#808080]">
                {papers.length} paper{papers.length !== 1 ? 's' : ''} in workspace
              </span>
            )}
          </div>

          {error && (
            <div className="bg-red-50 rounded-md px-4 py-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {result && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-[#4d4d4d]">
                  {result.references?.length ?? 0} reference{result.references?.length !== 1 ? 's' : ''} &mdash; {result.style}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleCopyAll}
                    className="px-3 py-1.5 text-xs font-medium rounded-md text-[#4d4d4d] hover:text-[#171717] transition-colors"
                    style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
                  >
                    {copyAllDone ? 'Copied!' : 'Copy All References'}
                  </button>
                  <button
                    onClick={handleDownload}
                    className="px-3 py-1.5 text-xs font-medium rounded-md text-[#4d4d4d] hover:text-[#171717] transition-colors"
                    style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
                  >
                    {downloadDone ? 'Downloaded!' : `Download as .txt`}
                  </button>
                </div>
              </div>

              <ol className="space-y-2 list-none">
                {result.references?.map((ref, index) => (
                  <li
                    key={ref.paper_id ?? index}
                    className="group flex items-start gap-3 bg-white rounded-lg px-4 py-3"
                    style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px, rgba(0,0,0,0.04) 0px 2px 2px' }}
                  >
                    <span className="flex-shrink-0 text-xs font-mono text-[#808080] mt-0.5 w-5 text-right select-none">
                      {index + 1}.
                    </span>
                    <p className="flex-1 text-sm text-[#4d4d4d] leading-relaxed font-serif">
                      {ref.formatted}
                    </p>
                    <CopyButton text={ref.formatted} />
                  </li>
                ))}
              </ol>
            </div>
          )}
        </>
      )}
    </div>
  );
}
